import {
  Stack,
  TabsProvider,
  Tab,
  Tabs,
  TabPanels,
  TabPanel,
  Box,
} from 'braid-design-system';
import { useEffect, useRef, type ReactNode } from 'react';

import type { ProfileBase, ProfileView } from '../../../types/profile.types';
import { ProfileViewCard } from '../ProfileViewCard/ProfileViewCard';

export type TabContent = {
  key: ProfileTabKey;
  title: string;
  component: ReactNode;
};

export type ProfileTabKey =
  | 'profile'
  | 'interactions'
  | 'no-interactions'
  | 'cv-preview';

export type TabChangeProps = {
  selectedTab: ProfileTabKey;
  onTabChange: (key: ProfileTabKey) => void;
};
interface Props {
  profile: ProfileView;
  profileUrl: string;
  tabs: TabContent[];
  tabChangeProps?: TabChangeProps;
  submitProfileReport?: (
    reason: string,
    comment: string,
    profileSlug: string,
  ) => Promise<boolean>;
  renderCardActions?: (profile: ProfileBase) => React.ReactNode;
  trackingCallbacks: {
    profileDisplayedCallback: () => void;
  };
  renderWidget?: () => ReactNode;
}
export const SignedInProfileView = ({
  profile,
  profileUrl,
  tabs,
  tabChangeProps,
  submitProfileReport,
  renderCardActions,
  trackingCallbacks: { profileDisplayedCallback },
  renderWidget,
}: Props) => {
  const shouldRenderContentInTabs = tabs.length > 1;
  const hasTrackedDisplayRef = useRef<string | null>(null);

  useEffect(() => {
    const profileKey = profile.profileGuid;
    if (hasTrackedDisplayRef.current !== profileKey) {
      profileDisplayedCallback();
      hasTrackedDisplayRef.current = profileKey;
    }
  }, [profileDisplayedCallback, profile.profileGuid]);

  if (tabs.length === 0) {
    return (
      <ProfileViewCard
        profile={profile}
        isSignedIn={true}
        profileUrl={profileUrl}
        submitProfileReport={submitProfileReport}
      />
    );
  }

  return (
    <Stack space="xlarge">
      <ProfileViewCard
        profile={profile}
        isSignedIn={true}
        profileUrl={profileUrl}
        renderActions={renderCardActions}
        onLastInteractionClick={() =>
          tabChangeProps?.onTabChange?.('interactions')
        }
        submitProfileReport={submitProfileReport}
      />

      {shouldRenderContentInTabs ? (
        <TabsProvider
          selectedItem={tabChangeProps?.selectedTab ?? 'profile'}
          onChange={(_, item) => {
            if (item !== undefined) {
              tabChangeProps?.onTabChange?.(item as ProfileTabKey);
            }
          }}
        >
          <Stack space="medium">
            <Tabs label="Profile content tabs" divider="full">
              {tabs.map(({ title, key }) => (
                <Tab item={key} key={key}>
                  {title}
                </Tab>
              ))}
            </Tabs>
            <TabPanels>
              {tabs.map(({ title, component }) => (
                <TabPanel key={title}>{component}</TabPanel>
              ))}
            </TabPanels>
          </Stack>
        </TabsProvider>
      ) : (
        <>{tabs[0].component}</>
      )}
      {renderWidget ? <Box>{renderWidget()}</Box> : null}
    </Stack>
  );
};
